/**
 * useVatCountry — SW-8538
 *
 * Session-scoped SWR fetch of `/api/user/geo-country`. Also supports a
 * development-only `?vat_country=XX` URL override that short-circuits
 * the API call (the backend supports the same override on its side —
 * this is just the client-side early exit so we don't waste a round trip
 * when an engineer is QAing a specific country).
 *
 * NEVER throws — on any fetch error returns `{ country: null, ... }`.
 * This keeps the VAT gate fail-closed without crashing checkout. As a
 * consequence, no `error` field is exposed: a fetch error is observable
 * only as `country === null`.
 *
 * See docs/vat-project/SW-8538-PRODUCTION-PLAN.md §4.
 */
import useSWR from "swr";
import * as Sentry from "@sentry/react";
import Auth from "../Auth";
import { VAT_COUNTRIES } from "../components/VatCheckout/vatCountries";

const GEO_ENDPOINT = "/api/user/geo-country";

// All 27 EU member states. VAT_COUNTRIES (in vatCountries.js) is the single
// source of truth for VAT scope. Keep the set private; export only an
// internal copy for tests.
const EU_SET = new Set(VAT_COUNTRIES.map((c) => c.code));

/** @internal Exposed ONLY for tests — do not import from app code. */
export const __EU_COUNTRIES = Array.from(EU_SET);

/**
 * Pull the `?vat_country=XX` override out of window.location.search.
 * Returns null if missing or if we're in a non-browser env (SSR safety).
 */
function getUrlOverride() {
  if (typeof window === "undefined" || !window.location) return null;
  try {
    const params = new URLSearchParams(window.location.search || "");
    const raw = params.get("vat_country");
    if (!raw) return null;
    return raw.toUpperCase();
  } catch (_) {
    return null;
  }
}

/**
 * SWR fetcher. Swallows errors → returns null so SWR caches the null
 * rather than retrying forever. The outer hook surfaces `country: null`
 * in that case, which the gate treats as non-EU. Because errors never
 * propagate, this hook deliberately does NOT expose an `error` field —
 * callers must observe failures via `country === null` (fail-closed).
 *
 * Note: POST, not GET — `Auth.fetchWithIdToken` sends the Firebase
 * idToken in the JSON body, which the browser strips from GET requests
 * (`TypeError: Request with GET/HEAD method cannot have body`). Using
 * the helper's default POST keeps the auth contract honest. See SW-8868.
 */
async function fetchCountry(url) {
  try {
    const data = await Auth.fetchWithIdToken(url, { method: "POST" });
    const country = data?.country;
    // `{ country: null, source: "unavailable" }` is a documented response
    // from /api/user/geo-country — geolocation legitimately failed. Treat
    // it as a clean fail-closed and don't spam Sentry.
    if (country === null || country === undefined) return null;
    if (typeof country !== "string" || country === "") {
      // Truly unexpected shape (number, object, empty string) — log to
      // Sentry so we can debug API contract drift. Still fail-closed.
      Sentry.captureMessage("useVatCountry: geo-country API returned unexpected shape", {
        level: "warning",
        tags: { feature: "vat.geo-country.shape" },
        extra: { data, countryType: typeof country },
      });
      return null;
    }
    return country.toUpperCase();
  } catch (err) {
    // Swallow but log — we still return null so the gate fails closed,
    // but we want the underlying error visible in Sentry for debugging.
    Sentry.captureException(err, {
      tags: { feature: "vat.geo-country.fetch" },
    });
    return null;
  }
}

/**
 * @returns {{
 *   country: string | null,
 *   isEU: boolean,
 *   isLoading: boolean,
 * }}
 */
export function useVatCountry() {
  const isDev = process.env.NODE_ENV !== "production";
  const override = isDev ? getUrlOverride() : null;

  // When an override is present we pass `null` as the SWR key, which
  // disables the fetch. `data` stays undefined but we ignore it and
  // resolve from the override synchronously.
  const swrKey = override ? null : GEO_ENDPOINT;
  const { data, isLoading } = useSWR(swrKey, fetchCountry, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
  });

  if (override) {
    return {
      country: override,
      isEU: EU_SET.has(override),
      isLoading: false,
    };
  }

  // No `error` branch — `fetchCountry` swallows all errors and returns
  // null, so SWR's `error` field is always null. Failed geo surfaces as
  // `country: null` → `isEU: false`.
  const country = typeof data === "string" ? data : null;
  return {
    country,
    isEU: country ? EU_SET.has(country) : false,
    isLoading: Boolean(isLoading),
  };
}

export default useVatCountry;
